"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Cpu, Bot, Globe as Globe2, ArrowRight, CircleCheck as CheckCircle2, CalendarCheck, ShieldAlert, Search, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
interface ServiceItem {
  id: string;
  title: string;
  tag: string;
  description: string;
  benefits: string[];
  to: string;
  iconName: "cpu" | "bot" | "globe";
}
const services: ServiceItem[] = [{
  id: "tech-seo",
  title: "Technical SEO",
  tag: "Crawlability & Architecture",
  description: "Deep diagnosis and remediation of indexing, rendering, schema architecture, and Core Web Vitals issues holding your search performance back.",
  benefits: ["Log file analysis & crawl budget efficiency", "JavaScript rendering & faceted navigation solutions", "Enterprise schema markup & site architecture"],
  to: "/TechnicalSeoPage",
  iconName: "cpu"
}, {
  id: "ai-search",
  title: "AI Search Optimisation",
  tag: "Next-Gen Discovery",
  description: "Practical strategies to position your brand across Google AI Overviews, Perplexity, and conversational search platforms without speculative guesswork.",
  benefits: ["Entity graph mapping & digital footprint alignment", "Topical authority modelling for LLM extraction", "Informational citation & source authority building"],
  to: "/AISearchOptimisationPage",
  iconName: "bot"
}, {
  id: "intl-seo",
  title: "International SEO",
  tag: "Global Search Scaling",
  description: "Cross-border organic search strategies tailored for multilingual markets, correct ccTLD/subfolder routing, and accurate hreflang execution.",
  benefits: ["Hreflang implementation & canonical validation", "Localised search intent & market gap analysis", "Multi-regional technical audits & performance tracking"],
  to: "/InternationalSeoPage",
  iconName: "globe"
}];
export default function SpecialisedServices() {
  const shouldReduceMotion = useReducedMotion();
  const renderIcon = (name: ServiceItem["iconName"]) => {
    switch (name) {
      case "cpu":
        return <Cpu className="size-6 text-primary" aria-hidden="true" />;
      case "bot":
        return <Bot className="size-6 text-primary" aria-hidden="true" />;
      case "globe":
        return <Globe2 className="size-6 text-primary" aria-hidden="true" />;
    }
  };
  return <section id="specialisedservicesoverview" data-nav="light" className="relative bg-background text-foreground py-20 md:py-28 overflow-hidden border-b border-border">
      {/* Background ambient accents */}
      <div className="pointer-events-none absolute -top-24 right-0 size-96 rounded-full bg-secondary/40 blur-3xl" aria-hidden="true" />
      <div className="pointer-events-none absolute -bottom-24 left-0 size-96 rounded-full bg-muted/60 blur-3xl" aria-hidden="true" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
        {/* Header section */}
        <div className="max-w-3xl mx-auto text-center mb-14 md:mb-20">
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="inline-flex items-center gap-2 mb-4">
            <Badge variant="outline" className="border-primary/20 bg-background text-primary px-3 py-1 font-medium">
              <Sparkles className="size-3.5 text-accent mr-1" aria-hidden="true" />
              Specialised Consulting Services
            </Badge>
          </motion.div>

          <motion.h2 initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground font-default">
            Specialised search consultancy for complex technical and global challenges
          </motion.h2>

          <motion.p initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed">
            You work directly with me to solve difficult crawl issues, capture next-generation
            AI discovery, and scale your organic presence across international markets. No junior
            account managers or layered agency overhead.
          </motion.p>
        </div>

        {/* Services 3-column grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 items-stretch mb-16">
          {services.map((service, index) => <div key={service.id} data-index={index} className="flex">
              <motion.div initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.12
          }} className="w-full flex">
                <Card className="flex flex-col justify-between w-full border-border bg-card hover:border-primary/30 transition-[border-color,box-shadow] duration-300 shadow-sm hover:shadow-md">
                  <div>
                    <CardHeader className="gap-3 pb-4">
                      <div className="flex items-center justify-between gap-2">
                        <div className="size-12 rounded-lg bg-secondary/50 flex items-center justify-center border border-border">
                          {renderIcon(service.iconName)}
                        </div>
                        <Badge variant="secondary" className="text-xs font-normal text-muted-foreground">
                          {service.tag}
                        </Badge>
                      </div>
                      <CardTitle className="text-xl sm:text-2xl font-semibold text-card-foreground font-default mt-2">
                        {service.title}
                      </CardTitle>
                      <CardDescription className="text-sm text-muted-foreground leading-relaxed pt-1">
                        {service.description}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="pt-2">
                      <div className="border-t border-border/70 pt-4">
                        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                          Key Deliverables
                        </p>
                        <ul className="space-y-2.5 text-sm text-card-foreground">
                          {service.benefits.map((benefit, benefitIndex) => <li key={benefitIndex} data-index={benefitIndex} className="flex items-start gap-2 text-xs sm:text-sm text-foreground/90">
                              <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" aria-hidden="true" />
                              <span>{benefit}</span>
                            </li>)}
                        </ul>
                      </div>
                    </CardContent>
                  </div>

                  <CardFooter className="pt-4 border-t border-border/70">
                    <Button variant="outline" className="w-full justify-between group text-foreground hover:text-primary hover:border-primary/40 font-medium" asChild>
                      <Link to={service.to}>
                        <span>Explore {service.title}</span>
                        <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>
            </div>)}
        </div>

        {/* Bottom CTA banner */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.3
      }} className="rounded-2xl border border-primary/20 bg-card p-8 md:p-10 shadow-sm flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="max-w-2xl text-center md:text-left">
            <h3 className="text-xl sm:text-2xl font-semibold text-foreground font-default">
              Need direct senior guidance on your search performance?
            </h3>
            <p className="mt-2 text-sm sm:text-base text-muted-foreground leading-relaxed">
              Book a consultation to discuss your technical architecture, international roadmap,
              or AI search footprint directly with me.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3 shrink-0 w-full md:w-auto">
            <Button size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 font-medium px-6 shadow-sm" asChild>
              <Link to="/ContactPage">
                <CalendarCheck className="size-4 mr-1.5" aria-hidden="true" />
                Book a Free SEO Consultation
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}